import { useApp } from '../../state/store';
import type { SessionRecord } from '../../engine/db';
import type { SessionSnapshot } from '../../core/types';

export interface SessionView {
  record: SessionRecord | undefined;
  snap: SessionSnapshot | undefined;
  total: number | null;
  /** Durable bytes. */
  saved: number;
  fraction: number | null;
  transferred: number;
  /** Bytes received more than once (retries, aborted tails). */
  overhead: number;
  complete: boolean;
  active: boolean;
  failed: boolean;
}

export function useSession(id: string | null): SessionView {
  const record = useApp((s) => (id ? s.records[id] : undefined));
  const snap = useApp((s) => (id ? s.snapshots[id] : undefined));
  const total = record?.totalSize ?? snap?.identity?.totalSize ?? null;
  const saved = snap?.counters.committedBytes ?? record?.committedBytes ?? 0;
  const transferred = snap?.counters.transferredBytes ?? 0;
  const readable = snap ? snap.readable.reduce((n, s) => n + (s.end - s.start), 0) : saved;
  const state = record?.state;
  return {
    record,
    snap,
    total,
    saved,
    fraction: total && total > 0 ? Math.min(1, saved / total) : total === 0 ? 1 : null,
    transferred,
    overhead: Math.max(0, transferred - readable),
    complete: state === 'complete',
    active: state === 'active' || state === 'probing',
    failed: state === 'recoverableError' || state === 'fatalError',
  };
}
